import * as v from "valibot";
import { compoundKey } from "@supply25/database/key";
import { ImprovementActionDecision } from "./result.ts";

// A version is the supplier's working copy until they submit it; only
// submitted versions ever reach the buyer
export const improvementActionStatus = {
  draft: "DRAFT",
  submitted: "SUBMITTED",
} as const;

export const ImprovementActionStatusSchema = v.picklist(
  Object.values(improvementActionStatus),
);

export type ImprovementActionStatus = v.InferOutput<
  typeof ImprovementActionStatusSchema
>;

// A supplier's improvement action on one failed reassessment answer. Each
// submission is a new version, so the buyer's decision stays with the version
// it judged; the newest version may be a draft, edited in place until it is
// submitted. Keyed under the result's URLID# prefix so one query returns the
// result, its evidence and every version together.
export const improvementActionReassessmentKey = {
  pk: compoundKey("ORGANISATION", "WORKSPACE"),
  sk: compoundKey("URLID", "IMPROVEMENT_ACTION", "VERSION"),
};

// Versions sort as strings in the key, so they are zero-padded
export const encodeVersion = (version: number) =>
  String(version).padStart(4, "0");

const actionEntries = {
  type: v.literal("IMPROVEMENT_ACTION_REASSESSMENT"),
  status: ImprovementActionStatusSchema,
  title: v.string(),
  answer: v.string(),
  completionDate: v.string(),
  authorId: v.string(),
  createdDate: v.string(),
  // When the version last changed, content or status. Versions from before
  // drafts existed were written once, so they have none
  lastUpdated: v.exactOptional(v.string()),
  // Versions from before drafts existed were created on submission
  submittedDate: v.exactOptional(v.string()),
  decision: v.exactOptional(v.enum(ImprovementActionDecision)),
  decisionReason: v.exactOptional(v.string()),
  decisionAuthorId: v.exactOptional(v.string()),
  decisionDate: v.exactOptional(v.string()),
};

export const ImprovementActionReassessmentDbSchema = v.object({
  ...actionEntries,
  pk: improvementActionReassessmentKey.pk,
  sk: improvementActionReassessmentKey.sk,
});

export const ImprovementActionReassessmentOutSchema = v.object({
  ...actionEntries,
  organisationId: v.string(),
  workspaceId: v.string(),
  urlId: v.string(),
  questionId: v.string(),
  version: v.number(),
});

export const ImprovementActionReassessmentSchema = v.pipe(
  ImprovementActionReassessmentDbSchema,
  v.transform(
    ({ pk, sk, ...rest }) =>
      ({
        ...rest,
        organisationId: pk.ORGANISATION,
        workspaceId: pk.WORKSPACE,
        urlId: sk.URLID,
        questionId: sk.IMPROVEMENT_ACTION,
        version: Number(sk.VERSION),
      }) satisfies v.InferInput<typeof ImprovementActionReassessmentOutSchema>,
  ),
);

export const ImprovementActionReassessmentsSchema = v.array(
  ImprovementActionReassessmentSchema,
);

export type ImprovementActionReassessment = v.InferOutput<
  typeof ImprovementActionReassessmentOutSchema
>;
export type ImprovementActionReassessmentIn = v.InferInput<
  typeof ImprovementActionReassessmentDbSchema
>;
